"use client";

import { useMemo } from "react";
import { useAdmin } from "@/lib/admin/context";
import { WindowAreaView } from "./WindowAreaView";
import type { UnpublishedWindow } from "@/lib/windows/types";

/**
 * WindowArea — renderuje obszar z oknami (drop-in dla `<WindowArea name="main" />`
 * w `L*` layout components, `HDefault.headerCol1..4`, `FDefault.footer*`).
 *
 * **Po unifikacji visit page (2026-05-12 v2):** zawsze renderuje `WindowAreaView`
 * (visitor render z `WindowContainer` + dynamicznym contentem z `WindowRenderer`).
 * Admin widzi **identyczną visit page** jak gość — drag&drop / edit happens
 * w drop-down panelu `LayoutAndWindowsPanel`, NIE przez switching tego komponentu
 * na osobny `WindowAreaEdit` slot UI.
 *
 * **Performance:** `Map<id, UnpublishedWindow>` budowane raz przez `useMemo`
 * z `pageWindows` — O(1) lookup per window'id zamiast O(N) `.find()`.
 */
export function WindowArea({ name }: WindowAreaProps) {
  const { windowsState, pageWindows, disabledAreas } = useAdmin();

  const windowsById = useMemo<Map<number, UnpublishedWindow>>(() => {
    const m = new Map<number, UnpublishedWindow>();
    for (const w of pageWindows) m.set(w.id, w);
    return m;
  }, [pageWindows]);

  /* Skip + collapse: gdy obszar jest disabled w bieżącym scope (cascade-resolved
     z imp_cms_catalog_area_state), NIE renderujemy markup'u strefy. Identyczne
     zachowanie dla wizyt i admina — admin zarządza enabled/disabled z
     `LayoutAndWindowsPanel` (drop-down overlay), nie z samej strony. */
  if ((disabledAreas as readonly string[]).includes(name)) {
    return null;
  }

  return (
    <WindowAreaView
      name={name}
      windowsState={windowsState}
      windowsById={windowsById}
    />
  );
}

export type WindowAreaProps = {
  name: string;
  /** Opcjonalny label — zachowany dla zgodności z miejscami wywołania (FDefault,
      HDefault, LSingleColumn, etc.). Aktualnie nieużywany w widoku — był
      używany przez usunięty `WindowAreaEdit` jako label placeholdera. */
  label?: string;
};
